import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import ToastStack from "src/components/shared/Alerts/Toast";
import { baseUrl } from "src/config/host";

const SOCKET_ENABLED = false;

let toastIdCounter = 0;

const SocketContext = createContext(null);
const ConnectionStatusContext = createContext(false);
const SocketTokenUpdateContext = createContext(() => {});
const SocketEventHubContext = createContext(null);

export const ToastContext = createContext({ toasts: [], addToast: () => {}, removeToast: () => {} });

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((props) => {
    const { event, onAction, title } = props;

    setToasts((prev) => {
      const existingBatchIndex = prev.findIndex((t) => t.isBatch);

      if (existingBatchIndex !== -1) {
        const updatedToasts = [...prev];
        const existing = updatedToasts[existingBatchIndex];

        if (existing.actions.some((a) => a.event === event && a.label === title)) return prev;

        updatedToasts[existingBatchIndex] = {
          ...existing,
          actions: [
            ...existing.actions,
            {
              event,
              label: title,
              handler: onAction,
              status: "pending",
            },
          ],
        };
        return updatedToasts;
      }

      const id = ++toastIdCounter;
      return [
        ...prev,
        {
          id,
          isBatch: true,
          actions: [
            {
              event,
              label: title,
              handler: onAction,
              status: "pending",
            },
          ],
          ...props,
        },
      ];
    });
  }, []);

  return <ToastContext.Provider value={{ toasts, addToast, removeToast }}>{children}</ToastContext.Provider>;
};

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem("accessToken"));
  const [alert, setAlert] = useState({ show: false, title: "", message: "", onConfirm: null });

  const [activeSocket, setActiveSocket] = useState(null);

  const eventHandlersRef = useRef(new Map());
  const eventDispatchersRef = useRef(new Map());

  useEffect(() => {
    if (!activeSocket) return;
    const dispatchers = eventDispatchersRef.current;
    dispatchers.forEach((dispatch, event) => {
      activeSocket.on(event, dispatch);
    });
    return () => {
      dispatchers.forEach((dispatch, event) => {
        activeSocket.off(event, dispatch);
      });
    };
  }, [activeSocket]);

  const subscribeToEvent = useCallback((event, handler) => {
    if (!eventHandlersRef.current.has(event)) {
      eventHandlersRef.current.set(event, new Set());
      const dispatch = (data) => {
        eventHandlersRef.current.get(event)?.forEach((fn) => fn(data));
      };
      eventDispatchersRef.current.set(event, dispatch);
      if (socketRef.current) socketRef.current.on(event, dispatch);
    }
    eventHandlersRef.current.get(event).add(handler);
    return () => {
      eventHandlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const triggerAlert = useCallback(({ title, message, onConfirm, critical = true }) => {
    if (critical) {
      setAlert({ show: true, title, message, onConfirm });
    }
  }, []);

  const updateToken = useCallback((newToken) => setToken(newToken), []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "accessToken") setToken(e.newValue);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!SOCKET_ENABLED || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setActiveSocket(null);
        setConnected(false);
      }
      return;
    }

    const socket = io(baseUrl, {
      transports: ["websocket", "polling"],
      path: "/api/socket.io",
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      auth: { token },
    });

    socketRef.current = socket;
    setActiveSocket(socket);

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onConnectError = (err) => {
      console.error("Socket Connection Error:", err.message);
      setConnected(false);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.disconnect();
      socketRef.current = null;
      setActiveSocket(null);
    };
  }, [token]);

  useEffect(() => {
    if (!socketRef.current) return;

    const interval = setInterval(() => {
      if (socketRef.current && !socketRef.current.connected) {
        console.log("Socket disconnected, attempting to reconnect...");
        socketRef.current.connect();
      }
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketRef.current]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && socketRef.current && !socketRef.current.connected) {
        console.log("Tab active, reconnecting socket...");

        socketRef.current.connect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return (
    <ToastProvider>
      <SocketContext.Provider value={{ socket: activeSocket, triggerAlert }}>
        <SocketTokenUpdateContext.Provider value={updateToken}>
          <ConnectionStatusContext.Provider value={connected}>
            <SocketEventHubContext.Provider value={subscribeToEvent}>
              {children}

              <ToastStack />

              {alert.show && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-md">
                  <div className="w-[90%] max-w-md rounded-[2rem] bg-white p-10 text-center shadow-2xl">
                    <div className="mb-6 flex justify-center">
                      <div className="flex h-20 w-20 animate-pulse items-center justify-center rounded-full bg-blue-50 text-4xl">⚙️</div>
                    </div>
                    <h2 className="mb-2 text-2xl font-bold text-gray-900">{alert.title}</h2>
                    <p className="mb-8 leading-relaxed text-gray-500">{alert.message}</p>
                    <button
                      onClick={() => {
                        alert.onConfirm?.();
                        setAlert((prev) => ({ ...prev, show: false }));
                      }}
                      className="w-full justify-end rounded-2xl bg-blue-600 py-4 text-lg font-bold text-white shadow-lg transition-all hover:scale-[1.02] active:scale-95"
                      style={{ backgroundColor: "var(--highlightColor)" }}>
                      Got it, sync now
                    </button>
                  </div>
                </div>
              )}
            </SocketEventHubContext.Provider>
          </ConnectionStatusContext.Provider>
        </SocketTokenUpdateContext.Provider>
      </SocketContext.Provider>
    </ToastProvider>
  );
};

export const useSocket = () => useContext(SocketContext)?.socket;
export const useTriggerAlert = () => useContext(SocketContext)?.triggerAlert;
export const useAddToast = () => useContext(ToastContext).addToast;
export const useSocketConnected = () => useContext(ConnectionStatusContext);
export const useUpdateSocketToken = () => useContext(SocketTokenUpdateContext);
export const useSubscribeToEvent = () => useContext(SocketEventHubContext);
